import { useRef, useEffect } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Shield, Clock, Users, CheckCircle, Star, BadgeCheck } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { t } from '@/i18n/translations';

const RSP_ITEMS = [
  {
    icon: CheckCircle,
    title: 'Responsible Sourcing Principles',
    text: 'Eksplicitna potrditev vseh RSP načel PMI. Operativni model vzpostavljen v skladu z mednarodnimi standardi.',
    highlight: true,
  },
  {
    icon: Clock,
    title: 'Delovni pogoji & počitek',
    text: 'Max 48h/teden z zagotovljenimi odmorki za vsakega člana ekipe. Nič kompromisov.',
    highlight: false,
  },
  {
    icon: Users,
    title: 'Visoka stabilnost ekipe',
    text: 'Sodelavci ostajajo z nami. Za PMI to pomeni stabilno ekipo brez nepotrebnih zamenjav.',
    highlight: false,
  },
];

const STAFFING_POINTS = [
  'Izključno profili 21+ let (videz 25+)',
  'Preverjena komunikacijska kompetentnost v HoReCa okolju',
  'Ustreznost estetskim in komunikacijskim standardom blagovne znamke',
  'Psihološko profiliranje pred aktivacijo (EQ-i 2.0)',
  'Brez kazenskih evidenc – popolna kadrovska čistoča',
];

export const ComplianceSection = () => {
  const sectionRef = useRef(null);
  const { lang } = useLanguage();
  const tx = t(lang).compliance;
  const RSP_ICONS = [CheckCircle, Clock, Users];

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo('.compliance-card',
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.6, ease: 'power3.out', stagger: 0.1,
          scrollTrigger: { trigger: sectionRef.current, start: 'top 80%' } }
      );
      gsap.fromTo('.staffing-item',
        { opacity: 0, x: -15 },
        { opacity: 1, x: 0, duration: 0.5, ease: 'power3.out', stagger: 0.08,
          scrollTrigger: { trigger: sectionRef.current, start: 'top 75%' } }
      );
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  return (
    <section
      id="compliance"
      ref={sectionRef}
      data-testid="compliance-section"
      className="scroll-section relative min-h-screen flex items-center overflow-hidden"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[500px] h-[400px] rounded-full bg-[#C87533]/4 blur-[100px]" />
      </div>

      <div className="section-content max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">

        {/* Compact header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#C87533]/30 bg-[#C87533]/10">
            <Shield className="w-3.5 h-3.5 text-[#C87533]" />
            <span className="font-mono text-xs text-[#C87533] uppercase tracking-wider">{tx.badge}</span>
          </div>
        </div>

        {/* Two-column layout */}
        <div className="grid lg:grid-cols-2 gap-6">

          {/* Left: title + RSP cards */}
          <div className="flex flex-col gap-4">
            <div>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-white mb-3">
                {tx.headline} <span className="text-copper-gradient">{tx.headlineAccent}</span>
              </h2>
              <p className="text-white/50 text-sm lg:text-base leading-relaxed">
                {tx.sub}
              </p>
            </div>

            {tx.rspItems.map((item, i) => {
              const Icon = RSP_ICONS[i];
              const highlight = i === 0;
              return (
                <div key={i}
                  className={`compliance-card rounded-xl p-5 border flex gap-4 items-start transition-all duration-300 ${
                    highlight ? 'border-[#C87533]/40 bg-[#C87533]/8' : 'border-white/8 bg-white/3'
                  }`}>
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    highlight ? 'bg-[#C87533]/20' : 'bg-white/6'
                  }`}>
                    <Icon className={`w-4 h-4 ${highlight ? 'text-[#C87533]' : 'text-white/50'}`} />
                  </div>
                  <div>
                    <h3 className="font-display text-base font-bold text-white mb-1.5">{item.title}</h3>
                    <p className="text-sm text-white/55 leading-relaxed">{item.text}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Right: PMI Compliant Staffing */}
          <div className="rounded-xl border border-white/8 bg-white/3 p-7 flex flex-col justify-between">
            <div>
              <div className="inline-flex items-center gap-2 mb-4 px-3 py-1 rounded-full border border-[#C87533]/30 bg-[#C87533]/10">
                <BadgeCheck className="w-3.5 h-3.5 text-[#C87533]" />
                <span className="font-mono text-xs text-[#C87533] uppercase tracking-wider">{tx.staffingBadge}</span>
              </div>
              <h3 className="font-display text-2xl lg:text-3xl font-bold text-white mb-3">
                {tx.staffingTitle} <span className="text-copper-gradient">{tx.staffingTitleAccent}</span>
              </h3>
              <p className="text-white/50 text-xs leading-relaxed mb-5">
                {tx.staffingBody}
              </p>
              <div className="staffing-list space-y-2.5">
                {tx.staffingPoints.map((point, i) => (
                  <div key={i} className="staffing-item flex items-start gap-2.5">
                    <div className="w-4 h-4 rounded-full bg-[#C87533]/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#C87533]" />
                    </div>
                    <span className="text-sm text-white/65 leading-relaxed">{point}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-5 pt-4 border-t border-white/8 flex items-center gap-2">
              <Star className="w-3.5 h-3.5 text-[#C87533]" />
              <span className="text-[10px] text-white/35 font-mono uppercase tracking-wider">
                {tx.footer}
              </span>
            </div>
          </div>

        </div>

      </div>

    </section>
  );
};
